(() => {
  class ExoticSlider {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-slider-track]');
      this.slides = Array.from(root.querySelectorAll('[data-slide]'));
      this.prevButton = root.querySelector('[data-slider-prev]');
      this.nextButton = root.querySelector('[data-slider-next]');
      this.pagination = root.querySelector('[data-slider-pagination]');
      this.currentPage = 0;
      this.resizeHandler = this.onResize.bind(this);
      this.scrollHandler = this.onScroll.bind(this);
      this.resetPages = this.resetPages.bind(this);

      if (!this.track || this.slides.length === 0) return;

      this.root.sliderApi = this;
      this.track.slider = this.track;
      this.track.sliderItems = this.slides;
      this.track.resetPages = this.resetPages;
      this.track.addEventListener('scroll', this.scrollHandler, { passive: true });

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.goToPage(this.currentPage - 1));
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.goToPage(this.currentPage + 1));
      }

      window.addEventListener('resize', this.resizeHandler);
      this.renderPagination();
      this.updateState(false);
    }

    resetPages() {
      this.slides = Array.from(this.root.querySelectorAll('[data-slide]'));
      this.track.sliderItems = this.slides;
      this.renderPagination();
      this.goToPage(this.currentPage, false);
    }

    get perPage() {
      const mobilePerPage = Number(this.root.dataset.mobilePerPage || 1);
      const desktopPerPage = Number(this.root.dataset.desktopPerPage || mobilePerPage);
      return window.matchMedia('(max-width: 749px)').matches ? mobilePerPage : desktopPerPage;
    }

    get pageStarts() {
      const pages = [];
      for (let index = 0; index < this.slides.length; index += this.perPage) {
        pages.push(index);
      }
      return pages;
    }

    get pageCount() {
      return Math.max(this.pageStarts.length, 1);
    }

    renderPagination() {
      if (!this.pagination) return;
      this.pagination.innerHTML = '';

      if (this.pageCount <= 1) {
        this.pagination.setAttribute('hidden', 'hidden');
        return;
      }

      this.pagination.removeAttribute('hidden');

      this.pageStarts.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'exotic-slider__dot';
        dot.setAttribute('aria-label', `Go to slide group ${index + 1}`);
        dot.addEventListener('click', () => this.goToPage(index));
        this.pagination.appendChild(dot);
      });
    }

    goToPage(page, smooth = true) {
      const boundedPage = Math.max(0, Math.min(page, this.pageCount - 1));
      const targetIndex = this.pageStarts[boundedPage] ?? 0;
      const targetSlide = this.slides[targetIndex];

      if (!targetSlide) return;

      this.currentPage = boundedPage;
      this.track.scrollTo({
        left: targetSlide.offsetLeft,
        behavior: smooth ? 'smooth' : 'auto',
      });
      this.updateState(false);
    }

    onResize() {
      this.renderPagination();
      this.goToPage(this.currentPage, false);
    }

    onScroll() {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        this.updateState(true);
      }, 70);
    }

    updateState(fromScroll) {
      const pageOffsets = this.pageStarts.map((startIndex) => {
        const slide = this.slides[startIndex];
        return slide ? slide.offsetLeft : 0;
      });
      const scrollLeft = this.track.scrollLeft;
      let nearestPage = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      pageOffsets.forEach((offset, index) => {
        const distance = Math.abs(scrollLeft - offset);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestPage = index;
        }
      });

      if (fromScroll) {
        this.currentPage = nearestPage;
      }

      if (this.prevButton) {
        this.prevButton.toggleAttribute('disabled', this.currentPage === 0);
      }

      if (this.nextButton) {
        this.nextButton.toggleAttribute('disabled', this.currentPage >= this.pageCount - 1);
      }

      if (this.pagination) {
        Array.from(this.pagination.children).forEach((dot, index) => {
          dot.classList.toggle('is-active', index === this.currentPage);
          if (index === this.currentPage) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }
    }
  }


  class SceneCarousel {
    constructor(root) {
      this.root = root;
      this.items = Array.from(root.querySelectorAll('[data-scene-carousel-item]'));
      this.dots = Array.from(root.querySelectorAll('[data-scene-carousel-dot]'));
      this.intervalMs = Number(root.dataset.interval || 5000);
      this.currentIndex = 0;

      if (this.items.length <= 1) return;

      this.boundPause = this.pause.bind(this);
      this.boundResume = this.resume.bind(this);

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.goTo(index);
          this.restart();
        });
      });

      this.root.addEventListener('mouseenter', this.boundPause);
      this.root.addEventListener('mouseleave', this.boundResume);
      this.root.addEventListener('focusin', this.boundPause);
      this.root.addEventListener('focusout', this.boundResume);

      this.start();
      this.update();
    }

    goTo(index) {
      const maxIndex = this.items.length - 1;
      this.currentIndex = Math.max(0, Math.min(index, maxIndex));
      this.update();
    }

    next() {
      this.currentIndex = (this.currentIndex + 1) % this.items.length;
      this.update();
    }

    update() {
      this.items.forEach((item, index) => {
        item.classList.toggle('is-active', index === this.currentIndex);
        item.setAttribute('aria-hidden', index === this.currentIndex ? 'false' : 'true');
      });

      this.dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === this.currentIndex);
        if (index === this.currentIndex) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    start() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.stop();
      this.timer = window.setInterval(() => this.next(), this.intervalMs);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    pause() {
      this.stop();
    }

    resume() {
      this.start();
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  const initSliders = (scope = document) => {
    scope.querySelectorAll('[data-exotic-slider]').forEach((sliderRoot) => {
      if (sliderRoot.sliderApi) return;
      new ExoticSlider(sliderRoot);
    });
  };

  const initSceneCarousels = (scope = document) => {
    scope.querySelectorAll('[data-scene-carousel]').forEach((carouselRoot) => {
      if (carouselRoot.carouselApi) return;
      carouselRoot.carouselApi = new SceneCarousel(carouselRoot);
    });
  };

  const initLookbookHotspots = (scope = document) => {
    const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

    scope.querySelectorAll('[data-lookbook-hotspot-link]').forEach((hotspot) => {
      if (hotspot.dataset.lookbookBound === 'true') return;
      hotspot.dataset.lookbookBound = 'true';

      hotspot.addEventListener('click', (event) => {
        if (!isCoarsePointer) return;

        if (!hotspot.classList.contains('is-active')) {
          event.preventDefault();

          scope.querySelectorAll('[data-lookbook-hotspot-link].is-active').forEach((activeHotspot) => {
            if (activeHotspot !== hotspot) {
              activeHotspot.classList.remove('is-active');
            }
          });

          hotspot.classList.add('is-active');
        }
      });
    });

    if (scope === document && !document.body.dataset.lookbookDismissBound) {
      document.body.dataset.lookbookDismissBound = 'true';

      document.addEventListener('click', (event) => {
        if (event.target.closest('[data-lookbook-hotspot-link]')) return;

        document.querySelectorAll('[data-lookbook-hotspot-link].is-active').forEach((hotspot) => {
          hotspot.classList.remove('is-active');
        });
      });
    }
  };

  const getTreeTvTarget = () => {
    const isDesktop = window.matchMedia('(min-width: 769px)').matches;

    if (isDesktop) {
      return (
        document.querySelector('.original-tree-section') ||
        document.querySelector('#tree-tv.original-tree-section') ||
        document.querySelector('#tree-tv') ||
        document.querySelector('.custom-modal-section')
      );
    }

    return (
      document.querySelector('.custom-modal-section') ||
      document.querySelector('#tree-tv.custom-modal-section') ||
      document.querySelector('#tree-tv') ||
      document.querySelector('.original-tree-section')
    );
  };

  const scrollTreeTvIntoView = () => {
    const treeSection = getTreeTvTarget();
    if (!treeSection) return false;

    const isDesktop = window.matchMedia('(min-width: 769px)').matches;
    const focusTarget =
      isDesktop
        ? treeSection.querySelector('.tree-video-positioner') || treeSection
        : treeSection;

    const rect = focusTarget.getBoundingClientRect();
    const scrollRoot = document.scrollingElement || document.documentElement;
    const absoluteTop = rect.top + window.pageYOffset;
    const stickyHeader =
      document.querySelector('#shopify-section-header .section-header') ||
      document.querySelector('.section-header') ||
      document.querySelector('.header-wrapper');
    const headerOffset = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
    const visibleViewportHeight = window.innerHeight - headerOffset;
    const centeredTop =
      absoluteTop -
      headerOffset -
      Math.max((visibleViewportHeight - rect.height) / 2, 0);
    const maxTop = Math.max(scrollRoot.scrollHeight - window.innerHeight, 0);

    window.scrollTo({
      top: Math.min(Math.max(centeredTop, 0), maxTop),
      behavior: 'smooth',
    });

    return true;
  };

  const scrollSectionIntoView = (selector) => {
    const section = document.querySelector(selector);
    if (!section) return false;

    const rect = section.getBoundingClientRect();
    const absoluteTop = rect.top + window.pageYOffset;
    const stickyHeader =
      document.querySelector('#shopify-section-header .section-header') ||
      document.querySelector('.section-header') ||
      document.querySelector('.header-wrapper');
    const headerOffset = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;

    window.scrollTo({
      top: Math.max(absoluteTop - headerOffset - 12, 0),
      behavior: 'smooth',
    });

    return true;
  };

  const closeTreeTvDrawers = () => {
    document.querySelectorAll('#Details-menu-drawer-container[open]').forEach((drawer) => {
      drawer.removeAttribute('open');
      const summary = drawer.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    });

    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden-tablet');
  };

  const initTreeTvLinks = (scope = document) => {
    scope
      .querySelectorAll('[data-tree-tv-link], [data-js-theater-flag], a[href$="#tree-tv"]')
      .forEach((link) => {
        if (link.dataset.treeTvBound === 'true') return;
        link.dataset.treeTvBound = 'true';

        link.addEventListener('click', (event) => {
          const canScrollInPage = Boolean(getTreeTvTarget());
          closeTreeTvDrawers();

          if (canScrollInPage) {
            event.preventDefault();
            window.setTimeout(() => {
              if (scrollTreeTvIntoView()) {
                window.history.replaceState(null, '', '#tree-tv');
              }
            }, 40);
          }
        });
      });
  };

  const initHomepageLookbookLinks = (scope = document) => {
    scope.querySelectorAll('a[href$="#homepage-lookbook"]').forEach((link) => {
      if (link.dataset.homeLookbookBound === 'true') return;
      link.dataset.homeLookbookBound = 'true';
      link.addEventListener('click', (event) => {
        if (scrollSectionIntoView('#homepage-lookbook')) {
          event.preventDefault();
          window.history.replaceState(null, '', '#homepage-lookbook');
        }
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initSliders();
    initSceneCarousels();
    initLookbookHotspots();
    initTreeTvLinks();
    initHomepageLookbookLinks();

    if (window.location.hash === '#tree-tv') {
      window.setTimeout(() => {
        scrollTreeTvIntoView();
      }, 180);
    }

    if (window.location.hash === '#homepage-lookbook') {
      window.setTimeout(() => {
        scrollSectionIntoView('#homepage-lookbook');
      }, 120);
    }
  });

  window.addEventListener('load', () => {
    if (window.location.hash === '#tree-tv') {
      window.setTimeout(() => {
        scrollTreeTvIntoView();
      }, 120);
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    initSliders(event.target);
    initSceneCarousels(event.target);
    initLookbookHotspots(event.target);
    initTreeTvLinks(event.target);
    initHomepageLookbookLinks(event.target);
  });
})();
